import React from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Clock, Server, Users, Eye, FileText, Calendar, ArrowRight, Info } from 'lucide-react';

// Safe text extraction - prevents React "Objects are not valid as child" crash
const txt = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') {
    return v.name || v.group || v.text || v.condition || v.gap || v.label || JSON.stringify(v);
  }
  return String(v);
};

const StatusBadge = ({ status }) => {
  const map = {
    'Ready_For_Approval': { bg: 'bg-emerald-500/10 border-emerald-500/30', text: 'text-emerald-400', label: 'Ready for Approval' },
    'Conditional_Approval': { bg: 'bg-amber-500/10 border-amber-500/30', text: 'text-amber-400', label: 'Conditional Approval' },
    'Return_To_Requester': { bg: 'bg-red-500/10 border-red-500/30', text: 'text-red-400', label: 'Return to Requester' },
  };
  const s = map[status] || map['Conditional_Approval'];
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${s.bg} ${s.text}`}>
      <Shield size={12} className="mr-1.5" />{s.label}
    </span>
  );
};

const Check = ({ pass, label, detail }) => {
  const [show, setShow] = React.useState(false);
  const hasDetail = detail && (detail.rating || detail.suggestion);
  return (
    <div
      className={`relative flex items-center justify-between py-1.5 ${hasDetail ? 'cursor-help' : ''}`}
      onMouseEnter={() => hasDetail && setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => hasDetail && setShow((s) => !s)}
    >
      <span className={`text-sm flex items-center gap-1 ${hasDetail ? 'text-slate-300 border-b border-dashed border-slate-600' : 'text-slate-400'}`}>
        {label}
        {hasDetail && <Info size={11} className="text-slate-500" />}
      </span>
      {pass === true ? <CheckCircle size={16} className="text-emerald-400" /> :
       pass === false ? <XCircle size={16} className="text-red-400" /> :
       <AlertTriangle size={16} className="text-amber-400" />}

      {show && hasDetail && (
        <div className="absolute z-50 left-0 top-full mt-1 w-72 rounded-lg border border-slate-700 bg-slate-950 p-3 shadow-xl">
          <div className="flex items-center gap-1.5 mb-1.5">
            {pass === true ? <CheckCircle size={13} className="text-emerald-400" /> :
             pass === false ? <XCircle size={13} className="text-red-400" /> :
             <AlertTriangle size={13} className="text-amber-400" />}
            <span className="text-xs font-semibold text-white">{label}</span>
          </div>
          {detail.rating && (
            <p className="text-[11px] text-slate-300 leading-snug mb-1.5">
              <span className="text-slate-500 font-medium">Why: </span>{txt(detail.rating)}
            </p>
          )}
          {detail.suggestion && (
            <p className="text-[11px] text-cyan-300 leading-snug">
              <span className="text-slate-500 font-medium">Fix: </span>{txt(detail.suggestion)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const Tag = ({ children, variant = 'red' }) => {
  const colors = { red: 'bg-red-500/10 text-red-400', amber: 'bg-amber-500/10 text-amber-400', green: 'bg-emerald-500/10 text-emerald-400', slate: 'bg-slate-800 text-slate-300' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[variant]}`}>{children}</span>;
};

const KV = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2 py-1">
    {Icon && <Icon size={14} className="text-slate-500 mt-0.5 shrink-0" />}
    <span className="text-slate-500 text-sm shrink-0">{label}</span>
    <span className="text-slate-200 text-sm ml-auto text-right">{value}</span>
  </div>
);

export default function CHGReviewResult({ reviewData }) {
  if (!reviewData) return null;

  const { assessment = {}, full_report } = reviewData;
  const ci = reviewData.impacted_cis || {};
  const sched = reviewData.schedule || {};
  const doc = reviewData.documentation || {};
  const fd = doc.field_details || {};
  const ag = reviewData.approval_guidance || assessment;

  const rec = ag.recommendation || assessment.recommendation || 'Conditional_Approval';

  // New category sub-scores from backend (chg-reviewer-v2). Falls back gracefully.
  const scores = reviewData.scores || null;
  const score = scores?.overall_score ?? reviewData.risk_benefit_score ?? assessment.risk_benefit_score ?? 50;
  const grade = scores?.overall_grade ?? null;
  const cats = scores?.categories || null;

  const gradeColor = (g) => ({
    A: 'text-emerald-400', B: 'text-lime-400', C: 'text-amber-400',
    D: 'text-orange-400', F: 'text-red-400',
  }[g] || 'text-slate-300');

  const barColor = (pct) =>
    pct >= 80 ? 'from-emerald-500 to-emerald-400'
    : pct >= 60 ? 'from-amber-500 to-amber-400'
    : 'from-red-500 to-red-400';

  const CategoryScore = ({ label, cat }) => {
    if (!cat) return null;
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-slate-300">{label}</span>
          <span className="text-xs font-semibold text-slate-200">
            {cat.score}/{cat.max}
            <span className={`ml-1.5 ${gradeColor(cat.grade)}`}>{cat.grade}</span>
          </span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full bg-gradient-to-r ${barColor(cat.percent)} transition-all duration-700`} style={{ width: `${cat.percent}%` }} />
        </div>
        {cat.deductions?.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {cat.deductions.map((d, i) => (
              <li key={i} className="text-[11px] text-slate-500 flex gap-1.5">
                <span className="text-red-400 font-medium shrink-0">{d.points}</span>
                <span>{d.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/80 p-5">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1">Policy Compliance Review</p>
            <h2 className="text-xl font-bold text-white">{reviewData?.metadata?.change_number || reviewData?.change_number || 'CHG Review'}</h2>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={rec} />
            <div className="text-right">
              <div className="flex items-baseline gap-1 justify-end">
                <span className="text-2xl font-bold text-white">{score}</span>
                {grade && <span className={`text-lg font-bold ${gradeColor(grade)}`}>{grade}</span>}
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Score / 100</div>
            </div>
          </div>
        </div>

        {/* Category sub-scores */}
        {cats ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <CategoryScore label="Configuration Items" cat={cats.impacted_cis} />
            <CategoryScore label="Schedule & Timing" cat={cats.schedule} />
            <CategoryScore label="Documentation" cat={cats.documentation} />
          </div>
        ) : (
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Overall</span>
              <span className="text-slate-400 font-medium">{score}%</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-700" style={{ width: `${score}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Grid Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card 1: Configuration Items */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Server size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Configuration Items</h3>
            <Tag variant="slate">{ci.attached_count || 0} attached</Tag>
          </div>

          {ci.attached_ci_details?.length > 0 && (
            <div className="mb-3">
              <div className="border border-slate-800 rounded overflow-hidden text-xs">
                <div className="grid grid-cols-3 bg-slate-800/50 px-2 py-1 font-medium text-slate-400">
                  <span>CI Name</span><span>Support Group</span><span>Used By</span>
                </div>
                {ci.attached_ci_details.map((row, i) => (
                  <div key={i} className="grid grid-cols-3 px-2 py-1 border-t border-slate-800/50">
                    <span className="text-slate-200 truncate">{row.name}</span>
                    <span className="text-slate-400">{row.support_group || '—'}</span>
                    <span className="text-slate-400">{row.used_by || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ci.missing_cis?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-red-400 font-medium mb-1.5">Missing CIs (Action Required)</p>
              <div className="flex flex-wrap gap-1.5">
                {ci.missing_cis.map((c, i) => <Tag key={i} variant="red">{typeof c === "object" ? (c.name || "") + " — " + (c.reason || c.relationship || "") : txt(c)}</Tag>)}
              </div>
            </div>
          )}

          {ci.impacted_applications?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-500 font-medium mb-1">Impacted Applications</p>
              <div className="flex flex-wrap gap-1.5">
                {ci.impacted_applications.map((a, i) => <Tag key={i} variant="amber">{txt(a)}</Tag>)}
              </div>
            </div>
          )}

          {ci.support_groups_to_notify?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-500 font-medium mb-1">Groups to Notify</p>
              {ci.support_groups_to_notify.map((g, i) => (
                <div key={i} className="flex items-start gap-1.5 py-0.5">
                  <Users size={12} className="text-slate-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300">{typeof g === 'object' ? `${g.group} — ${g.reason}` : g}</span>
                </div>
              ))}
            </div>
          )}

          {(ci.discovered_ci_table?.length > 0 || ci.missing_ci_table?.length > 0) && (
            <div className="mb-3">
              <p className="text-xs text-amber-400 font-medium mb-2">CIs to Review (Add if Impacted)</p>
              <div className="border border-slate-800 rounded overflow-hidden text-xs">
                <div className="grid grid-cols-2 bg-slate-800/50 px-2 py-1 font-medium text-slate-400">
                  <span>CI Name</span><span>Justification</span>
                </div>
                {(ci.missing_ci_table || ci.discovered_ci_table || []).map((row, i) => (
                  <div key={i} className="grid grid-cols-2 px-2 py-1 border-t border-slate-800/50">
                    <span className="text-slate-200">{row.name}</span>
                    <span className="text-slate-400">{row.justification}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ci.unregistered_systems?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-red-400 font-medium mb-2">Not in CMDB (Register Before Attaching)</p>
              <div className="border border-slate-800 rounded overflow-hidden text-xs">
                {ci.unregistered_systems.map((s, i) => (
                  <div key={i} className="px-2 py-1 border-t border-slate-800/50 first:border-t-0 text-slate-300">
                    {txt(s)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {ci.monitoring_gaps?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-amber-400 font-medium mb-1">Monitoring Gaps</p>
              {ci.monitoring_gaps.map((g, i) => (
                <div key={i} className="flex items-center gap-1.5 py-0.5">
                  <Eye size={12} className="text-amber-500" />
                  <span className="text-sm text-slate-400">{txt(g)}</span>
                </div>
              ))}
            </div>
          )}

          {ci.ci_accuracy_assessment && (
            <div className="mt-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <p className="text-xs text-amber-300 leading-relaxed"><AlertTriangle size={12} className="inline mr-1 text-amber-400" /><strong>CI Accuracy:</strong> {ci.ci_accuracy_assessment}</p>
            </div>
          )}
          {ci.recommendation && (
            <div className="mt-2 p-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-xs text-slate-300 leading-relaxed"><Info size={12} className="inline mr-1 text-slate-500" />{ci.recommendation}</p>
            </div>
          )}
        </div>

        {/* Card 2: Schedule & Timing */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-emerald-400" />
            <h3 className="text-sm font-semibold text-white">Schedule & Timing</h3>
          </div>

          <div className="space-y-0.5 mb-3">
            <KV icon={Clock} label="Window" value={`${sched.start_date_mst || '?'} → ${sched.end_date_mst || '?'}`} />
            <KV icon={CheckCircle} label="Maintenance Window" value={sched.in_maintenance_window ? '✓ Compliant' : '✗ Non-compliant'} />
          </div>

          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <div className="text-xs text-slate-500">Downtime</div>
              <div className={`text-sm font-medium mt-0.5 ${sched.downtime_highlight ? 'text-red-400' : 'text-emerald-400'}`}>
                {sched.downtime_declared || 'None'}
              </div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <div className="text-xs text-slate-500">Blackout</div>
              <div className={`text-sm font-medium mt-0.5 ${sched.blackout_overlap ? 'text-red-400' : 'text-emerald-400'}`}>
                {sched.blackout_overlap ? 'Conflict' : 'Clear'}
              </div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <div className="text-xs text-slate-500">Duration</div>
              <div className="text-sm font-medium mt-0.5 text-slate-200">{sched.implementation_duration || '?'}</div>
            </div>
          </div>
        </div>

        {/* Card 3: Documentation Completeness */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-red-400" />
            <h3 className="text-sm font-semibold text-white">Documentation Completeness</h3>
            <Tag variant="red">Needs Attention</Tag>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-slate-500 font-medium mb-2 uppercase tracking-wider">Description & Justification</p>
              <Check pass={doc.description_clarity === 'Adequate'} label="Description Clarity" detail={fd.description_clarity} />
              <Check pass={doc.justification_adequate} label="Business Justification" detail={fd.justification_adequate} />
              <Check pass={doc.business_impact_adequate} label="Impact Analysis" detail={fd.business_impact_adequate} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium mb-2 uppercase tracking-wider">Implementation & Planning</p>
              <Check pass={doc.implementation_plan_sound} label="Implementation Plan" detail={fd.implementation_plan_sound} />
              <Check pass={doc.business_impact_adequate} label="Business Impact" detail={fd.business_impact_adequate} />
              <Check pass={doc.dependencies_identified} label="Dependencies" detail={fd.dependencies_identified} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium mb-2 uppercase tracking-wider">Risk & Recovery</p>
              <Check pass={doc.risk_level_appropriate} label="Risk Level" detail={fd.risk_level_appropriate} />
              <Check pass={doc.backout_plan_viable} label="Backout Plan" detail={fd.backout_plan_viable} />
              <Check pass={doc.test_plan_adequate} label="Test Plan" detail={fd.test_plan_adequate} />
            </div>
          </div>

          {/* Gaps */}
          {doc.gaps?.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
              <p className="text-xs text-red-400 font-semibold mb-1.5">Identified Gaps</p>
              {doc.gaps.map((g, i) => (
                <div key={i} className="flex items-start gap-2 py-0.5">
                  <AlertTriangle size={12} className="text-red-400 mt-0.5 shrink-0" />
                  <span className="text-xs text-slate-400">{txt(g)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approval Guidance Footer */}
      {(ag.conditions?.length > 0 || ag.blocking_issues?.length > 0) && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
          <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
            <AlertTriangle size={14} />Conditions for Approval
          </h4>
          {ag.blocking_issues?.map((b, i) => (
            <div key={i} className="flex items-start gap-2 py-1">
              <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-300">{txt(b)}</span>
            </div>
          ))}
          {ag.conditions?.map((c, i) => {
            const ctxt = typeof c === "object" ? (c.condition || c.text || JSON.stringify(c)) : c;
            return (
            <div key={i} className="flex items-start gap-2 py-1">
              <ArrowRight size={14} className="text-amber-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-300">{ctxt}</span>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
